import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { askAI, restoreAIConversation } from "../api/ai";
import { getCurrentAIAllowance } from "../api/aiAllowance";
import { getApiError } from "../api/errors";
import { useAuth } from "./AuthContext";
import { createIdempotencyKey } from "../utils/format";
import { useTranslation } from "react-i18next";
import "../i18n";
import { boundedPageContext, resolveAINavigation } from "../copilot/pageContext";
import { entityOfferFromResponse, identitySelectionPayload } from "../utils/aiEntityResolution";
import { clearConversationPointer, clearConversationPointers, conversationScope, readConversationPointer, writeConversationPointer } from "../utils/aiConversationStorage";

const AIAssistantContext = createContext(null);

const responseTaskKey = (data) => JSON.stringify([data.conversation_id, data.task_id, data.task_version, data.task_hash,
  data.action?.result?.pending_edit?.edit_id, data.action?.result?.pending_edit?.revision]);
const messageTaskKey = (item) => responseTaskKey({ conversation_id: item.conversationId, task_id: item.taskId,
  task_version: item.taskVersion, task_hash: item.taskHash, action: item.action });

function restoredTaskMessage(data, confirmationLabel) {
  const entityResolution = entityOfferFromResponse(data);
  return { role: "ai", content: data.final_answer, agent: data.agent_used, steps: [], action: data.action,
    entityResolution, responseLanguage: data.response_language, conversationId: data.conversation_id,
    taskId: data.task_id, taskVersion: data.task_version, taskHash: data.task_hash,
    resourcePublicId: data.resource_public_id, originalMessage: confirmationLabel,
    idempotencyKey: createIdempotencyKey("ai-action"), confirming: false,
    requiresConfirmation: Boolean(data.requires_confirmation) && !entityResolution && !data.action?.result?.pending_edit };
}

export const AI_ASSISTANT_READ_SUGGESTIONS = [
  "Which products are low in stock?",
  "Show me the current stock for iPhone products.",
  "What does our warranty policy say?",
  "Find customer Ahmad by phone or email.",
  "Price 2 iPhone 16 units with a 5% discount.",
];

export const AI_ASSISTANT_ACTION_SUGGESTIONS = [
  "Create a quotation draft for Ahmad with 2 iPhone 16 units.",
];

export function AIAssistantProvider({ children }) {
  const { t } = useTranslation();
  const translationRef = useRef(t);
  translationRef.current = t;
  const { user, selectedWorkspace, selectedStore, canEdit } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [capabilities, setCapabilities] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const [hasPendingTask, setHasPendingTask] = useState(false);
  const [pageContext, setPageContext] = useState(null);
  const [navigationAction, setNavigationAction] = useState(null);
  const pageContextRef = useRef(null);
  const routeContextRef = useRef(null);
  const registrationRef = useRef(null);
  const pageContextScopeRef = useRef(null);
  const entityOfferRef = useRef(null);
  const identityRequestRef = useRef(null);
  const reorderRequestRef = useRef(null);
  const latestTaskRef = useRef(null);
  const rememberedScope = conversationScope(user, selectedWorkspace, selectedStore);
  const rememberTaskResponse = useCallback((data) => {
    writeConversationPointer(rememberedScope, data.conversation_id);
    if (data.task_id) {
      latestTaskRef.current = responseTaskKey(data);
      setHasPendingTask(!data.resource_public_id);
    }
  }, [rememberedScope]);

  const scopeKey = `${user?.public_id || ""}:${selectedWorkspace?.public_id || ""}:${selectedStore?.public_id || ""}`;
  const scopeKeyRef = useRef(scopeKey);
  const sessionEpochRef = useRef(0);
  const previousUserRef = useRef(user?.public_id);
  scopeKeyRef.current = scopeKey;

  useEffect(() => {
    scopeKeyRef.current = scopeKey;
    const epoch = ++sessionEpochRef.current;
    if (previousUserRef.current && previousUserRef.current !== user?.public_id) clearConversationPointers(previousUserRef.current);
    previousUserRef.current = user?.public_id;
    setOpen(false);
    setMessage("");
    setChat([]);
    setLoading(false);
    setError("");
    setCapabilities([]);
    setConversationId(null);
    setHasPendingTask(false);
    setRestoreFailed(false);
    const saved = readConversationPointer(rememberedScope);
    setRestoring(Boolean(saved));
    entityOfferRef.current = null;
    identityRequestRef.current = null;
    reorderRequestRef.current = null;
    latestTaskRef.current = null;
    if (pageContextScopeRef.current !== scopeKey) {
      pageContextRef.current = null;
      routeContextRef.current = null;
      registrationRef.current = null;
      setPageContext(null);
    }
    setNavigationAction(null);
    if (saved) {
      restoreAIConversation(saved).then(({ data }) => {
        if (scopeKeyRef.current !== scopeKey || sessionEpochRef.current !== epoch) return;
        if (data.conversation_id !== saved) throw new Error("Conversation restore identity mismatch");
        setConversationId(saved);
        const history = (data.messages || []).map((entry) => ({ role: entry.role === "assistant" ? "ai" : "user", content: entry.content, responseLanguage: entry.response_language }));
        const task = data.current_task;
        const current = task ? restoredTaskMessage(task, translationRef.current("assistant.confirm", { lng: task.response_language })) : null;
        if (task) {
          latestTaskRef.current = responseTaskKey(task);
          entityOfferRef.current = current.entityResolution;
        }
        setHasPendingTask(Boolean(data.has_pending_task));
        setChat(current ? [...history, current] : history);
      }).catch((requestError) => {
        if (scopeKeyRef.current !== scopeKey || sessionEpochRef.current !== epoch) return;
        if ([403, 404, 410, 422].includes(requestError?.response?.status)) {
          clearConversationPointer(rememberedScope);
        } else {
          setRestoreFailed(true);
          setError(translationRef.current("assistant.persistence.restoreError"));
        }
      }).finally(() => {
        if (scopeKeyRef.current === scopeKey && sessionEpochRef.current === epoch) setRestoring(false);
      });
    }
    if (selectedWorkspace?.public_id && selectedStore?.public_id) {
      getCurrentAIAllowance()
        .then((allowance) => {
          if (scopeKeyRef.current === scopeKey) {
            setCapabilities(allowance?.capabilities || []);
          }
        })
        .catch(() => {});
    }
  }, [scopeKey, rememberedScope, restoreAttempt, user?.public_id, selectedWorkspace?.public_id, selectedStore?.public_id]);

  const retryRestore = useCallback(() => setRestoreAttempt((value) => value + 1), []);
  const newChat = useCallback(() => {
    ++sessionEpochRef.current;
    clearConversationPointer(rememberedScope);
    setConversationId(null); setChat([]); setMessage(""); setError("");
    setLoading(false); setRestoring(false); setRestoreFailed(false); setHasPendingTask(false);
    latestTaskRef.current = null; entityOfferRef.current = null;
    identityRequestRef.current = null; reorderRequestRef.current = null;
    setNavigationAction(null);
  }, [rememberedScope]);

  const agentEnabled = capabilities.includes("ai.agent");
  const suggestions = useMemo(() => [
    ...AI_ASSISTANT_READ_SUGGESTIONS.map((value, index) => t(`assistant.suggestions.${index}`, { defaultValue: value })),
    ...(agentEnabled ? AI_ASSISTANT_ACTION_SUGGESTIONS.map((value, index) => t(`assistant.actionSuggestions.${index}`, { defaultValue: value })) : []),
  ], [agentEnabled, t]);

  const openAssistant = useCallback(() => setOpen(true), []);
  const closeAssistant = useCallback(() => setOpen(false), []);
  const clearError = useCallback(() => setError(""), []);
  const setAIRouteContext = useCallback((value) => {
    const next = boundedPageContext(value);
    pageContextScopeRef.current = scopeKey;
    routeContextRef.current = next;
    registrationRef.current = null;
    pageContextRef.current = next;
    setPageContext(next);
  }, [scopeKey]);
  const registerAIPageContext = useCallback((value) => {
    const next = boundedPageContext(value);
    if (!next) return () => {};
    const token = {};
    pageContextScopeRef.current = scopeKey;
    registrationRef.current = token;
    pageContextRef.current = next;
    setPageContext(next);
    return () => {
      if (registrationRef.current !== token) return;
      registrationRef.current = null;
      pageContextRef.current = routeContextRef.current;
      setPageContext(routeContextRef.current);
    };
  }, [scopeKey]);
  const focusAIAction = useCallback((actionId) => {
    const next = boundedPageContext({ ...pageContextRef.current, focused_action_id: actionId });
    if (next) { pageContextRef.current = next; setPageContext(next); }
  }, []);

  const sendMessage = useCallback(async (rawMessage) => {
    const normalized = String(rawMessage ?? message).trim();
    if (!normalized || loading || restoring || restoreFailed || scopeKeyRef.current !== scopeKey || identityRequestRef.current || reorderRequestRef.current || !selectedWorkspace?.public_id || !selectedStore?.public_id) {
      return;
    }

    const requestScopeKey = scopeKey;
    const requestEpoch = sessionEpochRef.current;
    setMessage("");
    setError("");
    setLoading(true);
    setChat((current) => [...current, { role: "user", content: normalized }]);

    try {
      const idempotencyKey = createIdempotencyKey("ai-action");
      const requestPageContext = pageContextScopeRef.current === requestScopeKey ? pageContextRef.current : null;
      const response = await askAI(normalized, { conversationId, ...(requestPageContext ? { pageContext: requestPageContext } : {}) });
      if (scopeKeyRef.current !== requestScopeKey || sessionEpochRef.current !== requestEpoch) return;
      setConversationId(response.data.conversation_id);
      rememberTaskResponse(response.data);
      const entityResolution = entityOfferFromResponse(response.data);
      if (response.data.task_id) entityOfferRef.current = entityResolution;
      if (resolveAINavigation(response.data.action)) {
        setNavigationAction({ action: response.data.action, scopeKey: requestScopeKey });
      }

      setChat((current) => [
        ...current,
        {
          role: "ai",
          content: response.data.final_answer,
          agent: response.data.agent_used,
          steps: response.data.steps || [],
          action: response.data.action || null,
          requiresConfirmation: Boolean(response.data.requires_confirmation) && !entityResolution && !response.data.action?.result?.pending_edit,
          conversationId: response.data.conversation_id,
          entityResolution,
          responseLanguage: response.data.response_language,
          resourcePublicId: response.data.resource_public_id || null,
          originalMessage: normalized,
          idempotencyKey,
          taskId: response.data.task_id || null,
          taskVersion: response.data.task_version || null,
          taskHash: response.data.task_hash || null,
          confirming: false,
        },
      ]);
    } catch (requestError) {
      if (scopeKeyRef.current === requestScopeKey && sessionEpochRef.current === requestEpoch) {
        setError(requestError?.code === "ERR_NETWORK" ? t("assistant.connectionError") : getApiError(requestError, t("assistant.answerError")));
      }
    } finally {
      if (scopeKeyRef.current === requestScopeKey && sessionEpochRef.current === requestEpoch) {
        setLoading(false);
      }
    }
  }, [conversationId, loading, restoring, restoreFailed, message, rememberTaskResponse, scopeKey, selectedStore?.public_id, selectedWorkspace?.public_id, t]);

  const isEntityOfferCurrent = useCallback((offer) => Boolean(offer && offer === entityOfferRef.current), []);

  const confirmIdentities = useCallback(async (index, selections) => {
    const item = chat[index];
    const offer = item?.entityResolution;
    if (!canEdit || loading || restoring || restoreFailed || scopeKeyRef.current !== scopeKey || identityRequestRef.current || reorderRequestRef.current || !isEntityOfferCurrent(offer)) return;
    const entitySelection = identitySelectionPayload(offer, selections);
    if (!entitySelection) return;
    const requestScopeKey = scopeKey;
    const requestEpoch = sessionEpochRef.current;
    const token = {};
    identityRequestRef.current = token;
    setError("");
    setLoading(true);
    setChat((current) => [...current, { role: "user", content: t("assistant.identity.confirm", { lng: item.responseLanguage }) }]);
    try {
      const response = await askAI(t("assistant.identity.confirm", { lng: item.responseLanguage }), {
        confirmAction: false,
        entitySelection,
        conversationId: offer.conversationId,
        taskId: offer.taskId, taskVersion: offer.taskVersion, taskHash: offer.taskHash,
        ...(pageContextScopeRef.current === requestScopeKey && pageContextRef.current ? { pageContext: pageContextRef.current } : {}),
      });
      if (scopeKeyRef.current !== requestScopeKey || sessionEpochRef.current !== requestEpoch) return;
      const entityResolution = entityOfferFromResponse(response.data);
      entityOfferRef.current = entityResolution;
      rememberTaskResponse(response.data);
      setConversationId(response.data.conversation_id);
      setChat((current) => [...current, {
        role: "ai", content: response.data.final_answer, agent: response.data.agent_used,
        steps: response.data.steps || [], action: response.data.action || null,
        entityResolution, responseLanguage: response.data.response_language,
        requiresConfirmation: Boolean(response.data.requires_confirmation) && !entityResolution && !response.data.action?.result?.pending_edit,
        conversationId: response.data.conversation_id,
        resourcePublicId: response.data.resource_public_id || null,
        taskId: response.data.task_id || null, taskVersion: response.data.task_version || null,
        taskHash: response.data.task_hash || null, originalMessage: item.originalMessage,
        idempotencyKey: createIdempotencyKey("ai-action"), confirming: false,
      }]);
    } catch (requestError) {
      if (scopeKeyRef.current === requestScopeKey && sessionEpochRef.current === requestEpoch) {
        setError(getApiError(requestError, t("assistant.identity.error", { lng: item.responseLanguage })));
      }
    } finally {
      if (identityRequestRef.current === token) {
        identityRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [canEdit, chat, isEntityOfferCurrent, loading, restoring, restoreFailed, rememberTaskResponse, scopeKey, t]);

  const submitReorderEdit = useCallback(async (index, action, targetId) => {
    const item = chat[index];
    const pending = item?.action?.result?.pending_edit;
    if (!pending || !canEdit || loading || restoring || restoreFailed || identityRequestRef.current || reorderRequestRef.current
      || scopeKeyRef.current !== scopeKey || latestTaskRef.current !== messageTaskKey(item)) return;
    if (action === "select_target" && !pending.targets.some((target) => target.target_id === targetId)) return;
    if (action === "apply" && (!pending.target_id || pending.issue)) return;
    const requestScopeKey = scopeKey;
    const requestEpoch = sessionEpochRef.current;
    const token = {};
    reorderRequestRef.current = token;
    setLoading(true);
    setError("");
    const display = t(`assistant.reorderEdit.${action}`, { lng: item.responseLanguage });
    try {
      const response = await askAI(display, {
        confirmAction: false, conversationId: item.conversationId, taskId: item.taskId,
        taskVersion: item.taskVersion, taskHash: item.taskHash,
        reorderEdit: { action, edit_id: pending.edit_id, revision: pending.revision, ...(action === "select_target" ? { target_id: targetId } : {}) },
      });
      if (scopeKeyRef.current !== requestScopeKey || sessionEpochRef.current !== requestEpoch) return;
      const data = response.data;
      latestTaskRef.current = data.task_id ? responseTaskKey(data) : null;
      rememberTaskResponse(data);
      entityOfferRef.current = entityOfferFromResponse(data);
      setConversationId(data.conversation_id);
      setChat((current) => [...current, { role: "user", content: display }, {
        role: "ai", content: data.final_answer, agent: data.agent_used, steps: data.steps || [], action: data.action || null,
        responseLanguage: data.response_language, conversationId: data.conversation_id,
        requiresConfirmation: Boolean(data.requires_confirmation) && !data.action?.result?.pending_edit,
        taskId: data.task_id, taskVersion: data.task_version, taskHash: data.task_hash,
        resourcePublicId: data.resource_public_id || null, originalMessage: item.originalMessage,
        idempotencyKey: createIdempotencyKey("ai-action"), confirming: false,
      }]);
    } catch (requestError) {
      if (scopeKeyRef.current === requestScopeKey && sessionEpochRef.current === requestEpoch) setError(getApiError(requestError, t("assistant.reorderEdit.error", { lng: item.responseLanguage })));
    } finally {
      if (reorderRequestRef.current === token) { reorderRequestRef.current = null; setLoading(false); }
    }
  }, [canEdit, chat, loading, restoring, restoreFailed, rememberTaskResponse, scopeKey, t]);

  const confirmAction = useCallback(async (index) => {
    const item = chat[index];
    if (!item?.requiresConfirmation || item.entityResolution || item.action?.result?.pending_edit || item.confirming || !canEdit || loading || restoring || restoreFailed || scopeKeyRef.current !== scopeKey || latestTaskRef.current !== messageTaskKey(item) || identityRequestRef.current || reorderRequestRef.current) return;

    const requestScopeKey = scopeKey;
    const requestEpoch = sessionEpochRef.current;
    setError("");
    setChat((current) => current.map((entry, entryIndex) =>
      entryIndex === index ? { ...entry, confirming: true } : entry
    ));

    try {
      const response = await askAI(item.originalMessage, {
        confirmAction: true,
        idempotencyKey: item.idempotencyKey,
        conversationId: item.conversationId,
        taskId: item.taskId,
        taskVersion: item.taskVersion,
        taskHash: item.taskHash,
        ...(pageContextScopeRef.current === requestScopeKey && pageContextRef.current ? { pageContext: pageContextRef.current } : {}),
      });
      if (scopeKeyRef.current !== requestScopeKey || sessionEpochRef.current !== requestEpoch) return;
      rememberTaskResponse(response.data);
      const entityResolution = entityOfferFromResponse(response.data);
      if (response.data.task_id) entityOfferRef.current = entityResolution;

      setChat((current) => current.map((entry, entryIndex) =>
        entryIndex === index
          ? {
              ...entry,
              content: response.data.final_answer,
              agent: response.data.agent_used,
              steps: response.data.steps || [],
              action: response.data.action || null,
              requiresConfirmation: Boolean(response.data.requires_confirmation) && !entityResolution && !response.data.action?.result?.pending_edit,
              conversationId: response.data.conversation_id,
              entityResolution,
              responseLanguage: response.data.response_language,
              resourcePublicId: response.data.resource_public_id || null,
              confirming: false,
              confirmed: !response.data.requires_confirmation,
              taskId: response.data.task_id || item.taskId,
              taskVersion: response.data.task_version || item.taskVersion,
              taskHash: response.data.task_hash || item.taskHash,
            }
          : entry
      ));
    } catch (requestError) {
      if (scopeKeyRef.current !== requestScopeKey || sessionEpochRef.current !== requestEpoch) return;
      setChat((current) => current.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, confirming: false } : entry
      ));
      setError(requestError?.code === "ERR_NETWORK" ? t("assistant.connectionError") : getApiError(requestError, t("assistant.confirmError")));
    }
  }, [canEdit, chat, loading, restoring, restoreFailed, rememberTaskResponse, scopeKey, t]);

  const value = useMemo(() => ({
    open,
    message,
    setMessage,
    chat,
    loading: loading || restoring,
    restoreFailed,
    retryRestore,
    newChat,
    hasPendingTask,
    error,
    openAssistant,
    closeAssistant,
    clearError,
    sendMessage,
    confirmAction,
    confirmIdentities,
    submitReorderEdit,
    isEntityOfferCurrent,
    capabilities,
    agentEnabled,
    suggestions,
    pageContext,
    setAIRouteContext,
    registerAIPageContext,
    focusAIAction,
    navigationAction,
    contextScopeKey: scopeKey,
  }), [
    chat,
    capabilities,
    clearError,
    closeAssistant,
    confirmAction,
    confirmIdentities,
    submitReorderEdit,
    isEntityOfferCurrent,
    error,
    loading,
    restoring, restoreFailed, retryRestore, newChat, hasPendingTask,
    message,
    open,
    openAssistant,
    agentEnabled,
    suggestions,
    sendMessage,
    pageContext, setAIRouteContext, registerAIPageContext, focusAIAction, navigationAction, scopeKey,
  ]);

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

export function useAIAssistant() {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error("useAIAssistant must be used inside AIAssistantProvider");
  }
  return context;
}

// Optional for isolated page rendering; the application provider owns the lease.
export function useAIPageContext(value) {
  const context = useContext(AIAssistantContext);
  const register = context?.registerAIPageContext;
  const signature = JSON.stringify(value);
  const scope = context?.contextScopeKey;
  const previous = useRef({ scope, signature });
  useEffect(() => {
    const retainedFromOldScope = previous.current.scope !== scope && previous.current.signature === signature;
    previous.current = { scope, signature };
    const next = JSON.parse(signature);
    // Existing dialog state may outlive a store switch. It must not re-assert
    // that old identity until another entity/context is explicitly opened.
    if (retainedFromOldScope && next?.entity_public_id) return undefined;
    return register?.(next);
  }, [register, signature, scope]);
  return context?.focusAIAction || (() => {});
}
